import { useCallback, type FC } from 'react';

import { useIssueBreakdownSubscription } from '../../../../../services/issue-breakdown';
import type {
	OnNextStatusData,
	OnNextSuggestionData,
} from '../../../../../services/issue-breakdown/types';
import { useIssueBreakdown } from '../../../context';

type Props = {
	onComplete?: () => void;
};

export const SubscribeToIssueBreakdown: FC<Props> = ({ onComplete }) => {
	const [
		{ channelId, headerPromptValue, acceptedChildIssues, rejectedChildIssues, issueTypeIds },
		{
			updateStreamingStatus,
			setStreamingError,
			setChannelId,
			addSuggestedIssue,
			setSelectedIssueIndex,
		},
	] = useIssueBreakdown();

	const onNextStatus = useCallback(
		(data: OnNextStatusData) => {
			if (data.status === 'COMPLETE') {
				updateStreamingStatus(false);
				onComplete?.();
				setSelectedIssueIndex(0);
			} else {
				updateStreamingStatus(true, data.status);
				data.channelId && setChannelId(data.channelId);
			}
		},
		[onComplete, setChannelId, setSelectedIssueIndex, updateStreamingStatus],
	);

	const onNextSuggestion = useCallback(
		({ summary, description, issueTypeId }: OnNextSuggestionData) => {
			addSuggestedIssue({ summary, description, issueTypeId });
		},
		[addSuggestedIssue],
	);

	const onSuggestionError = useCallback(() => {
		setStreamingError();
	}, [setStreamingError]);

	const onError = useCallback(() => {
		setStreamingError();
	}, [setStreamingError]);

	const onTimeout = useCallback(() => {
		setStreamingError();
	}, [setStreamingError]);

	const query = {
		channelId,
		additionalContext: headerPromptValue,
		acceptedChildIssues,
		rejectedChildIssues,
		issueTypeIds,
	};
	const params = { query, onNextStatus, onNextSuggestion, onSuggestionError, onError, onTimeout };
	useIssueBreakdownSubscription(params);
	return null;
};
